import React, { useState, useEffect, useCallback } from "react";
import MetricForm from "../Metric-Form/MetricForm";
import MetricTable from "../Metric-Table/MetricTable";
import Dialog from "../FormDialog/Dialog";
import ErrorDialog from "../ErrorDialog/ErrorDialog";

import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getMetricHeaders } from "../../ApiHeaders/apiHeadersTimeseries";
import { datadogRum } from "@datadog/browser-rum";
import LoadingStyle from "../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";



interface MetricSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}
interface MetadataType {
  award_types: any[];
  product_types: any[];
  agg_functions: {
    agg_func_id: number;
    category: string;
    from_granularity: string;
    to_granularity: string;
    agg_method: string;
    description: string;
    unit: string;
  }[];
}

const MetricSetup: React.FC<MetricSetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false
}) => {


    const [metrics, setMetrics] = useState<any[]>([]);

    const [selectedMetric, setSelectedMetric] = useState<any | null>(null);

    const [error, setError] = useState<{ title: string; details: string } | null>(
        null
      );

    const [isFormDirty, setIsFormDirty] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [selectedRow, setSelectedRow] = useState<string | null>(null);


    const [duplicateGroups, setDuplicateGroups] = useState<Record<string, any[]>>({});

    const [metadata, setMetadata] = useState<MetadataType | null>(null);




    const [dialog, setDialog] = useState<{
        open: boolean;
        title: string;
        message: React.ReactNode;
        confirmButtonText: string;
        cancelButtonText?: string;
        onConfirm: () => void;
        onCancel?: () => void;
      }>({
        open: false,
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
      });
    
      const closeDialog = () => {
        setDialog({
          open: false,
          title: "",
          message: "",
          confirmButtonText: "",
          onConfirm: () => {},
        });
      };

    const handleApiError = (err: any, fallbackTitle: string) => {
        let title = fallbackTitle;
        let details = "An unknown error occurred";

        const statusCode = err.response?.status;
        const errorResponse = err.response?.data?.error;

        const backendMessage =
            errorResponse?.error?.message || errorResponse?.message;

        switch (statusCode) {
            case 400:
            title = "Bad Request";
            details = backendMessage || "The data you submitted is invalid.";
            break;
            case 401:
            title = "Acces Denied";
            details =
                "You're not authorized to access this content. Please log in or check your access permissions.";
            break;
            case 403:
            title = "Access Denied";
            details = "You need permission to access this site.";
            break;
            case 404:
            title = "Not Found";
            details =
                backendMessage || "The requested resource could not be found.";
            break;
            case 500:
            title = "Internal Server Error";
            details =
                backendMessage ||
                "Server error while fetching data. Please try again later.";
            break;
            default:
            if (
                err.message?.includes("Network Error") ||
                err.message?.includes("timeout")
            ) {
                title = "Network Error";
                details =
                "Unable to connect to the server. Check your internet connection.";
            } else {
                title = fallbackTitle;
                details =
                backendMessage || err.message || "An unexpected error occurred.";
            }
            break;
        }

        setError({ title, details });
        datadogRum.addError(err, { title, details });
    };


    const fetchMetadata = async () => {
      const startTime = performance.now();
  
      try {
        const headers = await getMetricHeaders();
        const response = await apiClientTimeseries.get("/metric-config/metadata", {
          headers,
        });
  
        const metadata = response?.data?.response?.data;
  
        if (metadata) {
          setMetadata(metadata);
  
          datadogRum.addAction("Fetched Metadata Successfully", {
            awardTypes: metadata.award_types?.length || 0,
            productTypes: metadata.product_types?.length || 0,
            aggFunctions: metadata.agg_functions?.length || 0,
          });
        } else {
          throw new Error("Invalid metadata format");
        }
      } catch (err) {
        console.error("Error fetching metadata:", err);
        // setError("Failed to fetch metadata");
        setError({
          title: "Metadata Error",
          details: "Failed to fetch metadata",
        });        
  
        datadogRum.addError("Failed to fetch metadata", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
  
        datadogRum.addAction("Metadata API Response Time", {
          duration: endTime - startTime,
        });
      }
    };

    useEffect(() => {
      fetchMetadata();
    }, []);


    // Build 6-field duplicate detection key
    const makeKey = (m: any) =>
      `${m.metric_name}|${m.iso_code}|${m.category}|${m.market_type}|${m.fuel_type}|${m.product}`;

      // Group rows by 6-field composite key
      const groupBySixFields = (metrics: any[]) => {
      const groups: Record<string, any[]> = {};
      metrics.forEach((m) => {
        const key = makeKey(m);
        if (!groups[key]) groups[key] = [];
        groups[key].push(m);
      });
      return groups;
      };

      // Reduce groups: keep max 2 entries only, first 2 occurrences
      const reduceGroupsToDuplicates = (groups: Record<string, any[]>) => {
      const result: Record<string, any[]> = {};

      Object.keys(groups).forEach((key) => {
        const rows = groups[key];

        // Only keep first 2 occurrences as duplicates
        if (rows.length >= 2) {
          result[key] = [rows[0], rows[1]];
        } else {
          result[key] = [rows[0]];
        }
      });

      return result;
    };



    const fetchMetrics = useCallback(async () => {
      try {
        setLoading(true);
    
        const headers = await getMetricHeaders();
        const response = await apiClientTimeseries.get(`/metric-config/`, { headers });
    
        const allMetrics = response?.data?.response?.data || [];
    
        // STEP 1: group by name, iso, category, market, fuel, product
        const grouped = groupBySixFields(allMetrics);
    
        // STEP 2: reduce groups to max 2 entries only
        const reducedGroups = reduceGroupsToDuplicates(grouped);
    
        setDuplicateGroups(reducedGroups);
    
        // STEP 3: show only FIRST entry in table
        const tableRows = Object.values(reducedGroups).map((arr) => arr[0]);
    
        setMetrics(tableRows);
    
      } catch (err) {
        handleApiError(err, "Failed to fetch metrics");
      } finally {
        setLoading(false);
      }
    }, []);
    

    useEffect(() => {
    fetchMetrics();
    }, [fetchMetrics]);


    // A single logical save can span two metric rows (one per source), so each
    // call takes `silent` to suppress its own dialog. The form reports the whole
    // save once via handleSaveSuccess instead of one dialog per request.
    const handleSaveSuccess = (message: string) => {
      setDialog({
        open: true,
        title: "Success",
        message,
        confirmButtonText: "Close",
        onConfirm: () => {
          closeDialog();
          setSelectedMetric(null);
        },
      });
    };

    const handleAddMetric = async (
      newMetric: any,
      opts: { silent?: boolean } = {}
    ): Promise<any | false> => {
      const metricWithAuditFields = { ...newMetric };
      const headers = await getMetricHeaders();

      try {
        //  Create metric
        const createdMetricResponse = await apiClientTimeseries.post("/metric-config/", metricWithAuditFields, { headers });

        //Refresh metric list
        await fetchMetrics();

        // Extract the created metric from either the standard array response or a
        // single-object response so downstream metadata can use its metric_id.
        const responseData = createdMetricResponse.data?.response?.data;
        const createdMetric = (Array.isArray(responseData) ? responseData[0] : responseData) || metricWithAuditFields;
        const metricName = createdMetric.metric_name || newMetric.metric_name;

        datadogRum.addAction("Metric added", { metric: metricName });

        if (!opts.silent) {
          setDialog({
            open: true,
            title: "Success",
            message: `Metric "${metricName}" successfully created.`,
            confirmButtonText: "Close",
            onConfirm: closeDialog,
          });
        }

        return createdMetric;
      } catch (err) {
        handleApiError(err, "Failed to Create Metric");
        return false;
      }
    };


    const handleEditMetric = async (
      updatedMetric: any,
      opts: { silent?: boolean } = {}
    ): Promise<boolean> => {
      const headers = await getMetricHeaders ();
      if (!selectedMetric) return false;

      try {
        const metricToUpdate = { ...updatedMetric };
        await apiClientTimeseries.put(
          `/metric-config/`,
          metricToUpdate,
          { headers }
        );
        await fetchMetrics();

        if (!opts.silent) {
          setDialog({
            open: true,
            title: "Success",
            message: `Metric "${updatedMetric.metric_name}" successfully updated.`,
            confirmButtonText: "Close",
            onConfirm: () => {
              closeDialog();
              setSelectedMetric(null);
            },
          });
        }

        datadogRum.addAction("Metric updated", {
          metric: updatedMetric.metric_name,
        });
        return true;
      } catch (err) {
        handleApiError(err, "Failed to Update Metric");
        return false;
      }
    };

    const handleDeleteMetric = async (metricId: number): Promise<boolean> => {
      try {
        const headers = await getMetricHeaders();

        await apiClientTimeseries.delete(`/metric-config/${metricId}`, { headers });

        datadogRum.addAction("Metric Deleted", { metricId });

        // Refresh table data
        await fetchMetrics();

        // Clear form selection if needed
        setSelectedMetric(null);
        setSelectedRow(null);

        return true;
      } catch (err) {
        handleApiError(err, "Failed to delete metric");
        return false;
      }
    };



    const handleRowSelect = (metric: any) => {
      const key = makeKey(metric);
    
      // Retrieve 1 or 2 entries for this key
      const selected = duplicateGroups[key] || [metric];
    
      if (isFormDirty || isCreateMode) {
        setDialog({
          open: true,
          title: "Unsaved Changes",
          message: "You have unsaved changes. Discard them?",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          onConfirm: () => {
            closeDialog();
          
            // STEP 1: EXIT CREATE / DIRTY MODE FIRST
            setIsFormDirty(false);
            setIsCreateMode(false);
          
            // STEP 2: CLEAR ANY CREATE STATE
            setSelectedMetric(null);
            setSelectedRow(null);
          
            // STEP 3: NEXT TICK → APPLY SELECTION
            setTimeout(() => {
              setSelectedMetric(selected.map(m => ({ ...m })));
              setSelectedRow(key);
            }, 0);
          },    
          onCancel: closeDialog,
        });
    } else {
      setSelectedMetric(null);
      setSelectedRow(null);
    
      setTimeout(() => {
        setSelectedMetric(selected.map(m => ({ ...m })));
        setSelectedRow(key);
      }, 0);
    }
    };
    
    

    
    const onClearSelection = () => {
        if (isFormDirty) {
          setDialog({
            open: true,
            title: "Unsaved Changes",
            message:
              "You have unsaved changes. Are you sure you want to discard them?",
            // confirmButtonText: "Discard Changes",
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            onConfirm: () => {
              setIsFormDirty(false);
              setSelectedRow(null);
              setSelectedRow(null);
              closeDialog();
            },
            onCancel: closeDialog,
          });
        } else {
          setSelectedMetric(null);
          setSelectedRow(null);
        }
      };

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <MetricTable
            metrics={metrics}
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
            onRowSelect={handleRowSelect}
            selectedRow={selectedRow}
        />
      </div>
      <SplitDivider {...split.dividerProps} />
      <div
        className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md"
      >
        <MetricForm 
        metadata={metadata}
        onSaveMetric={handleAddMetric}
        onSaveChanges={handleEditMetric}
        metric={selectedMetric}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        handleApiError={handleApiError}
        onClearSelection={onClearSelection}
        onDirtyChange={setIsFormDirty}
        onModeChange={(mode) => setIsCreateMode(mode === 'create')}
        onDeleteMetric={handleDeleteMetric}
        onSaveSuccess={handleSaveSuccess}
        canDelete={canDelete}
        />
      </div>

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onConfirm={dialog.onConfirm}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default MetricSetup;
